import { useEffect, useState, useMemo, useRef } from "react";
import {
  Mail,
  Linkedin,
  Globe,
  Calendar,
  MoreVertical,
  Trash2,
  Edit3,
  Archive,
  Send,
  X,
  SquarePlus,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Coffee,
  MessageSquare,
  HelpCircle,
  StickyNote,
  Building,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavbar } from "./DashboardLayout";
import AddContactModal, { Contact, EmailStatus } from "../components/addContact";
import AddCoffeeChat from "../components/addCoffeeChat";

/* =========================================================================
   COLD EMAIL SECTION
   Mirrors src/pages/Coldemails.tsx — same design, same Firestore collection
   ========================================================================= */

const SECTION_STORAGE_KEY = "networkingColdEmailCollapsedSections";
const SECTION_KEYS = ["draft", "sent", "replied"] as const;
type SectionKey = typeof SECTION_KEYS[number];

function EmailStatusBadge({ status }: { status: EmailStatus }) {
  const styles: Record<EmailStatus, string> = {
    draft: "bg-amber-100 text-amber-700 border-amber-200",
    sent: "bg-blue-100 text-blue-700 border-blue-200",
    replied: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  const labels: Record<EmailStatus, string> = {
    draft: "Draft",
    sent: "Sent",
    replied: "Replied",
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-md border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function EmailEditorModal({
  contact,
  onClose,
  onSave,
}: {
  contact: Contact;
  onClose: () => void;
  onSave: (content: string, status: EmailStatus) => void;
}) {
  const [content, setContent] = useState(contact.emailContent || "");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(content.length, content.length);
    }
  }, []);

  const handleSave = async (newStatus: EmailStatus) => {
    setSaving(true);
    await onSave(content, newStatus);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showClose={false}
        className="flex max-w-4xl flex-col gap-0 overflow-hidden p-0 shadow-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div>
            <DialogTitle className="text-xl">Email to {contact.name}</DialogTitle>
            <DialogDescription className="mt-1">{contact.role}</DialogDescription>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-cocoa hover:bg-cocoa/10"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your cold email here..."
            className="w-full h-full min-h-[400px] px-4 py-3 rounded-lg border-2 border-cocoa/20 focus:border-caramel focus:ring-2 focus:ring-caramel/20 outline-none transition resize-none font-sans text-[15px] leading-relaxed"
          />
        </div>

        <div className="border-t border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-cocoa">
            {content.length} characters
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleSave("draft")}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 disabled:opacity-50"
            >
              Save Draft
            </button>
            <button
              onClick={() => handleSave("sent")}
              className="px-4 py-2 rounded-lg bg-caramel text-white hover:opacity-90  flex items-center gap-2"
            >
              <Send size={16} />
              Mark as Sent
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({
  contact,
  onEdit,
  onDelete,
  onArchive,
  onOpenEditor,
  onStatusChange,
}: {
  contact: Contact;
  onEdit: () => void;
  onDelete: () => void;
  onArchive: () => void;
  onOpenEditor: () => void;
  onStatusChange: (status: EmailStatus) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setShowStatusMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const emailPreview = contact.emailContent
    ? contact.emailContent.split("\n")[0].substring(0, 60) + (contact.emailContent.length > 60 ? "..." : "")
    : "No content yet";

  return (
    <div
      className="rounded-2xl border border-cocoa/10 bg-cream p-4 shadow-sm hover:shadow-md transition cursor-pointer"
      onClick={onOpenEditor}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-[15px] font-semibold text-espresso">{contact.name}</h3>
          <p className="text-sm text-cocoa">{contact.role}</p>
        </div>

        <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded-md text-cocoa hover:bg-oat"
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-cocoa/10 py-1 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
              >
                <Edit3 size={14} />
                Edit Details
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
              >
                <Archive size={14} />
                {contact.archived ? "Unarchive" : "Archive"}
              </button>

              <div
                className="relative group"
                onMouseEnter={() => setShowStatusMenu(true)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStatusMenu(!showStatusMenu);
                  }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
                >
                  <span className="flex items-center gap-2">
                    <ChevronRight size={14} />
                    Edit Status
                  </span>
                  <ChevronUp size={14} className={`transition-transform ${showStatusMenu ? "rotate-180" : "rotate-90"}`} />
                </button>

                {showStatusMenu && (
                  <div className="absolute left-full top-0 ml-1 w-32 bg-white rounded-lg shadow-lg border border-cocoa/10 py-1 z-20">
                    {(["draft", "sent", "replied"] as EmailStatus[]).map((statusOption) => (
                      <button
                        key={statusOption}
                        onClick={(e) => {
                          e.stopPropagation();
                          onStatusChange(statusOption);
                          setShowMenu(false);
                          setShowStatusMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-cocoa/5 ${
                          contact.status === statusOption ? "text-caramel hover:bg-cocoa/5 font-medium" : "text-espresso"
                        }`}
                      >
                        {statusOption === "draft" && "Draft"}
                        {statusOption === "sent" && "Sent"}
                        {statusOption === "replied" && "Replied"}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {contact.email && (
          <div className="flex items-center gap-2 text-sm text-cocoa">
            <Mail size={14} className="shrink-0" />
            <span className="truncate">{contact.email}</span>
          </div>
        )}
        {contact.linkedin && (
          <div className="flex items-center gap-2 text-sm text-cocoa">
            <Linkedin size={14} className="shrink-0" />
            <a
              href={contact.linkedin.startsWith("http") ? contact.linkedin : `https://${contact.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-caramel"
              onClick={(e) => e.stopPropagation()}
            >
              LinkedIn
            </a>
          </div>
        )}
        {contact.website && (
          <div className="flex items-center gap-2 text-sm text-cocoa">
            <Globe size={14} className="shrink-0" />
            <a
              href={contact.website.startsWith("http") ? contact.website : `https://${contact.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-caramel"
              onClick={(e) => e.stopPropagation()}
            >
              Website
            </a>
          </div>
        )}
      </div>

      {contact.emailContent && (
        <div className="mt-3 p-2 bg-white rounded-lg">
          <p className="text-xs text-cocoa italic line-clamp-1">
            "{emailPreview}"
          </p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <EmailStatusBadge status={contact.status} />
        {contact.sentDate && (
          <div className="flex items-center gap-1.5 text-xs text-cocoa">
            <Calendar size={12} />
            <span>{new Date(contact.sentDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================================
   COFFEE CHAT SECTION
   Mirrors src/pages/CoffeeChats.tsx — same design, same Firestore collection
   ========================================================================= */

type ChatStatus = "pending" | "sent" | "done";

type CoffeeChat = {
  id: string;
  name: string;
  company: string;
  role: string;
  email?: string;
  linkedin?: string;
  status: ChatStatus;
  message: string;
  questions: string[];
  notes: string;
  scheduledDate?: string;
  createdAt: any;
};

function ChatStatusBadge({ status }: { status: ChatStatus }) {
  const styles: Record<ChatStatus, string> = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    sent: "bg-blue-100 text-blue-700 border-blue-200",
    done: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  const labels: Record<ChatStatus, string> = {
    pending: "Pending",
    sent: "Sent",
    done: "Done",
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-md border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function CoffeeChatCard({
  chat,
  onDelete,
  onEdit,
  onViewDetails,
}: {
  chat: CoffeeChat;
  onDelete: () => void;
  onEdit: () => void;
  onViewDetails: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div
      className="rounded-2xl border border-cocoa/10 bg-cream p-5 shadow-sm hover:shadow-md transition cursor-pointer"
      onClick={onViewDetails}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-[16px] font-semibold text-espresso mb-1">{chat.name}</h3>
          <div className="flex items-center gap-2 text-sm text-cocoa mb-2">
            <Building size={14} className="shrink-0" />
            <span className="truncate">{chat.role} at {chat.company}</span>
          </div>
        </div>

        <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded-md text-cocoa hover:bg-oat"
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-cocoa/10 py-1 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
              >
                <Edit3 size={14} />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        {chat.email && (
          <div className="flex items-center gap-2 text-xs text-cocoa">
            <Mail size={12} className="shrink-0" />
            <span className="truncate">{chat.email}</span>
          </div>
        )}
        {chat.linkedin && (
          <div className="flex items-center gap-2 text-xs text-cocoa">
            <Linkedin size={12} className="shrink-0" />
            <a
              href={chat.linkedin.startsWith("http") ? chat.linkedin : `https://${chat.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-[#d97857]"
              onClick={(e) => e.stopPropagation()}
            >
              LinkedIn
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-cocoa/5">
        <div className="flex items-center gap-1.5 text-xs text-cocoa">
          <MessageSquare size={12} />
          <span>Message {chat.message ? "✓" : "✗"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-cocoa">
          <HelpCircle size={12} />
          <span>{chat.questions.length} questions</span>
        </div>
        {chat.notes && (
          <div className="flex items-center gap-1.5 text-xs text-cocoa">
            <StickyNote size={12} />
            <span>Notes</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <ChatStatusBadge status={chat.status} />
        {chat.scheduledDate && (
          <div className="flex items-center gap-1.5 text-xs text-cocoa">
            <Calendar size={12} />
            <span>{new Date(chat.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatDetailsModal({
  chat,
  onClose,
  onEdit,
}: {
  chat: CoffeeChat;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="flex max-w-3xl flex-col gap-0 overflow-hidden p-0 shadow-2xl">
        <div className="border-b border-cocoa/10 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-xl">{chat.name}</DialogTitle>
              <DialogDescription className="mt-1">{chat.role} at {chat.company}</DialogDescription>
              <div className="flex items-center gap-3 mt-2">
                <ChatStatusBadge status={chat.status} />
                {chat.scheduledDate && (
                  <div className="flex items-center gap-1.5 text-xs text-cocoa">
                    <Calendar size={12} />
                    <span>Scheduled: {new Date(chat.scheduledDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-espresso hover:bg-cocoa/5"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3">
              Contact Information
            </h3>
            <div className="space-y-2 bg-cream rounded-lg p-4">
              {chat.email && (
                <div className="flex items-center gap-2 text-sm text-cocoa">
                  <Mail size={14} className="shrink-0" />
                  <span>{chat.email}</span>
                </div>
              )}
              {chat.linkedin && (
                <div className="flex items-center gap-2 text-sm text-cocoa">
                  <Linkedin size={14} className="shrink-0" />
                  <a
                    href={chat.linkedin.startsWith("http") ? chat.linkedin : `https://${chat.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-caramel underline"
                  >
                    LinkedIn
                  </a>
                </div>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
              <MessageSquare size={14} />
              Draft Message
            </h3>
            {chat.message ? (
              <div className="bg-cream rounded-lg p-4 border border-cocoa/10">
                <p className="text-sm text-espresso whitespace-pre-wrap leading-relaxed">
                  {chat.message}
                </p>
              </div>
            ) : (
              <div className="bg-cream rounded-lg p-4 border-2 border-dashed border-sand text-center">
                <p className="text-sm text-cocoa italic">No message drafted yet</p>
              </div>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
              <HelpCircle size={14} />
              Questions to Ask ({chat.questions.length})
            </h3>
            {chat.questions.length > 0 ? (
              <ul className="space-y-2">
                {chat.questions.map((question, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 p-3 bg-cream rounded-lg border border-cocoa/10"
                  >
                    <span className="text-sm font-semibold text-cocoa shrink-0">
                      {index + 1}.
                    </span>
                    <span className="text-sm text-espresso">{question}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="bg-cream rounded-lg p-4 border-2 border-dashed border-sand text-center">
                <p className="text-sm text-cocoa italic">No questions added yet</p>
              </div>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
              <StickyNote size={14} />
              Notes
            </h3>
            {chat.notes ? (
              <div className="bg-cream rounded-lg p-4 border border-cocoa/10">
                <p className="text-sm text-espresso whitespace-pre-wrap leading-relaxed">
                  {chat.notes}
                </p>
              </div>
            ) : (
              <div className="bg-cream rounded-lg p-4 border-2 border-dashed border-sand text-center">
                <p className="text-sm text-cocoa italic">No notes yet</p>
              </div>
            )}
          </section>
        </div>

        <div className="border-t border-cocoa/10 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 transition"
          >
            Close
          </button>
          <button
            onClick={() => {
              onEdit();
              onClose();
            }}
            className="px-6 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition flex items-center gap-2"
          >
            <Edit3 size={16} />
            Edit
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
   ADD TYPE CHOOSER — lets the user pick what kind of item to add
   ========================================================================= */

function AddTypeChooser({
  onClose,
  onPickColdEmail,
  onPickCoffeeChat,
}: {
  onClose: () => void;
  onPickColdEmail: () => void;
  onPickCoffeeChat: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="block max-w-md p-0 shadow-2xl">
        <div className="border-b border-cocoa/20 px-6 py-4 flex items-center justify-between">
          <div>
            <DialogTitle className="text-xl">Add to Networking</DialogTitle>
            <DialogDescription className="mt-1">What would you like to add?</DialogDescription>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-espresso hover:bg-cocoa/5"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={onPickColdEmail}
            className="flex flex-col items-center gap-2 rounded-2xl border border-cocoa/20 bg-cream p-6 hover:border-caramel hover:shadow-md transition"
          >
            <Mail size={28} className="text-caramel" />
            <span className="text-sm font-semibold text-espresso">Cold Email</span>
            <span className="text-xs text-cocoa text-center">Reach out to a new contact</span>
          </button>
          <button
            onClick={onPickCoffeeChat}
            className="flex flex-col items-center gap-2 rounded-2xl border border-cocoa/20 bg-cream p-6 hover:border-caramel hover:shadow-md transition"
          >
            <Coffee size={28} className="text-caramel" />
            <span className="text-sm font-semibold text-espresso">Coffee Chat</span>
            <span className="text-xs text-cocoa text-center">Schedule a networking conversation</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
   MAIN PAGE
   ========================================================================= */

export default function Networking() {
  const { setNavbarActions } = useNavbar();
  const [user, setUser] = useState(auth.currentUser);
  const [loading, setLoading] = useState(true);

  // Which "add" flow is open
  const [showTypeChooser, setShowTypeChooser] = useState(false);

  // Cold email state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    draft: false,
    sent: false,
    replied: false,
  });
  const persistedSectionsRef = useRef<Set<SectionKey>>(new Set<SectionKey>());

  // Coffee chat state
  const [chats, setChats] = useState<CoffeeChat[]>([]);
  const [showAddChat, setShowAddChat] = useState(false);
  const [editingChat, setEditingChat] = useState<CoffeeChat | null>(null);
  const [selectedChat, setSelectedChat] = useState<CoffeeChat | null>(null);

  // Shared archive section (archived cold emails + completed coffee chats)
  const [showArchive, setShowArchive] = useState(false);

  // AUTH
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // NAVBAR ACTION
  useEffect(() => {
    setNavbarActions(
      <button
        onClick={() => setShowTypeChooser(true)}
        className="inline-flex items-center gap-2 rounded-lg bg-caramel px-3 py-1 text-white hover:opacity-90 transition"
      >
        <SquarePlus size={16} />
        Add
      </button>
    );

    return () => setNavbarActions(null);
  }, []);

  // RESTORE COLLAPSED SECTION PREFS
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(SECTION_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Partial<Record<string, boolean>>;
      const restored: Partial<Record<SectionKey, boolean>> = {};
      SECTION_KEYS.forEach((key) => {
        if (typeof parsed?.[key] === "boolean") {
          restored[key] = parsed[key];
          persistedSectionsRef.current.add(key);
        }
      });
      if (Object.keys(restored).length > 0) {
        setCollapsedSections((prev) => ({ ...prev, ...restored }));
      }
    } catch (error) {
      console.error("Failed to restore section preferences", error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(collapsedSections));
  }, [collapsedSections]);

  // COLD EMAIL LISTENER
  useEffect(() => {
    if (!user) {
      setContacts([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, "users", user.uid, "coldEmails"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const contactsData: Contact[] = [];
      snapshot.forEach((docSnap) => {
        contactsData.push({ id: docSnap.id, ...docSnap.data() } as Contact);
      });
      setContacts(contactsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // COFFEE CHAT LISTENER
  useEffect(() => {
    if (!user) {
      setChats([]);
      return;
    }

    const q = query(
      collection(db, "users", user.uid, "coffeeChats"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatsData: CoffeeChat[] = [];
      snapshot.forEach((docSnap) => {
        chatsData.push({ id: docSnap.id, ...docSnap.data() } as CoffeeChat);
      });
      setChats(chatsData);
    });

    return () => unsubscribe();
  }, [user]);

  /* ---------- COLD EMAIL HANDLERS ---------- */

  const handleAddContact = async (contactData: Omit<Contact, "id" | "createdAt">) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const cleanedData: any = {
        name: contactData.name,
        role: contactData.role,
        status: contactData.status,
        emailContent: contactData.emailContent,
        archived: contactData.archived,
      };

      if (contactData.email) cleanedData.email = contactData.email;
      if (contactData.linkedin) cleanedData.linkedin = contactData.linkedin;
      if (contactData.website) cleanedData.website = contactData.website;
      if (contactData.sentDate) cleanedData.sentDate = contactData.sentDate;

      if (editingContact) {
        await updateDoc(doc(db, "users", currentUser.uid, "coldEmails", editingContact.id), cleanedData);
        setEditingContact(null);
      } else {
        await addDoc(collection(db, "users", currentUser.uid, "coldEmails"), {
          ...cleanedData,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Error adding/updating contact:", error);
      alert("Failed to save contact");
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      await deleteDoc(doc(db, "users", currentUser.uid, "coldEmails", contactId));
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert("Failed to delete contact");
    }
  };

  const handleArchiveContact = async (contact: Contact) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      await updateDoc(doc(db, "users", currentUser.uid, "coldEmails", contact.id), {
        archived: !contact.archived,
      });
    } catch (error) {
      console.error("Error archiving contact:", error);
      alert("Failed to archive contact");
    }
  };

  const handleChangeStatus = async (contact: Contact, status: EmailStatus) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    if (contact.status === status) {
      return;
    }

    try {
      const updateData: any = { status };

      if (status === "sent" && !contact.sentDate) {
        updateData.sentDate = new Date().toISOString();
      }

      await updateDoc(doc(db, "users", currentUser.uid, "coldEmails", contact.id), updateData);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const handleSaveEmail = async (contactId: string, content: string, status: EmailStatus) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const updateData: any = {
        emailContent: content,
        status: status,
      };

      if (status === "sent" && !selectedContact?.sentDate) {
        updateData.sentDate = new Date().toISOString();
      }

      await updateDoc(doc(db, "users", currentUser.uid, "coldEmails", contactId), updateData);
    } catch (error) {
      console.error("Error saving email:", error);
      alert("Failed to save email");
    }
  };

  const activeContacts = useMemo(() => contacts.filter((c) => !c.archived), [contacts]);
  const archivedContacts = useMemo(() => contacts.filter((c) => c.archived), [contacts]);

  const groupedContacts = useMemo(() => {
    const draft = activeContacts.filter((c) => c.status === "draft");
    const sent = activeContacts.filter((c) => c.status === "sent");
    const replied = activeContacts.filter((c) => c.status === "replied");
    return { draft, sent, replied };
  }, [activeContacts]);

  useEffect(() => {
    setCollapsedSections((prev) => {
      let changed = false;
      const next = { ...prev };
      const sections: [SectionKey, number][] = [
        ["draft", groupedContacts.draft.length],
        ["sent", groupedContacts.sent.length],
        ["replied", groupedContacts.replied.length],
      ];

      sections.forEach(([key, count]) => {
        if (!persistedSectionsRef.current.has(key)) {
          const desiredState = count === 0;
          if (prev[key] !== desiredState) {
            next[key] = desiredState;
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [groupedContacts.draft.length, groupedContacts.sent.length, groupedContacts.replied.length, archivedContacts.length]);

  const toggleSection = (key: SectionKey) => {
    persistedSectionsRef.current.add(key);
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /* ---------- COFFEE CHAT HANDLERS ---------- */

  const handleDeleteChat = async (chatId: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      await deleteDoc(doc(db, "users", currentUser.uid, "coffeeChats", chatId));
    } catch (error) {
      console.error("Error deleting chat:", error);
      alert("Failed to delete chat");
    }
  };

  const groupedChats = useMemo(() => {
    const pending = chats.filter((c) => c.status === "pending");
    const sent = chats.filter((c) => c.status === "sent");
    const done = chats.filter((c) => c.status === "done");
    return { pending, sent, done };
  }, [chats]);

  /* ---------- RENDER ---------- */

  const renderContactGrid = (list: Contact[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.map((contact) => (
        <ContactCard
          key={contact.id}
          contact={contact}
          onEdit={() => {
            setEditingContact(contact);
            setShowAddContact(true);
          }}
          onDelete={() => handleDeleteContact(contact.id)}
          onArchive={() => handleArchiveContact(contact)}
          onOpenEditor={() => {
            setSelectedContact(contact);
            setShowEmailEditor(true);
          }}
          onStatusChange={(status) => handleChangeStatus(contact, status)}
        />
      ))}
    </div>
  );

  const renderChatGrid = (list: CoffeeChat[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.map((chat) => (
        <CoffeeChatCard
          key={chat.id}
          chat={chat}
          onDelete={() => handleDeleteChat(chat.id)}
          onEdit={() => {
            setEditingChat(chat);
            setShowAddChat(true);
          }}
          onViewDetails={() => setSelectedChat(chat)}
        />
      ))}
    </div>
  );

  const hasContacts = activeContacts.length > 0;
  const hasChats = groupedChats.pending.length > 0 || groupedChats.sent.length > 0;

  return (
    <div className="w-full">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-cocoa">Loading networking...</div>
        </div>
      ) : (
        <div className="space-y-12">
          {/* ============ COLD EMAILS ============ */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <Mail size={18} className="text-caramel" />
              <h2 className="text-lg font-semibold text-espresso">Cold Emails</h2>
            </div>
            {/* <p className="text-sm text-cocoa mb-5">People you're reaching out to over email.</p>*/}

            {!hasContacts ? (
              <div className="text-center py-12 bg-cream rounded-2xl border-2 border-dashed border-sand">
                <Mail size={40} className="mx-auto text-caramel mb-4" />
                <p className="text-cocoa mb-4">No cold emails yet.</p>
                <button
                  onClick={() => {
                    setEditingContact(null);
                    setShowAddContact(true);
                  }}
                  className="inline-flex items-center gap-2 text-caramel hover:underline"
                >
                  <SquarePlus size={16} />
                  Add your first contact
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {groupedContacts.draft.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => toggleSection("draft")}
                      className="flex w-full items-center justify-between mb-4"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
                        <h3 className="text-[15px] font-semibold text-espresso">
                          Drafts <span className="text-cocoa font-normal">({groupedContacts.draft.length})</span>
                        </h3>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`text-cocoa transition-transform ${collapsedSections.draft ? "-rotate-90" : ""}`}
                      />
                    </button>
                    {!collapsedSections.draft && renderContactGrid(groupedContacts.draft)}
                  </div>
                )}

                {groupedContacts.sent.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => toggleSection("sent")}
                      className="flex w-full items-center justify-between mb-4"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500" />
                        <h3 className="text-[15px] font-semibold text-espresso">
                          Sent <span className="text-cocoa font-normal">({groupedContacts.sent.length})</span>
                        </h3>
                      </div>
                      <ChevronRight
                        size={16}
                        className={`text-cocoa transition-transform ${!collapsedSections.sent ? "rotate-90" : ""}`}
                      />
                    </button>
                    {!collapsedSections.sent && renderContactGrid(groupedContacts.sent)}
                  </div>
                )}

                {groupedContacts.replied.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => toggleSection("replied")}
                      className="flex w-full items-center justify-between mb-4"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        <h3 className="text-[15px] font-semibold text-espresso">
                          Replied <span className="text-cocoa font-normal">({groupedContacts.replied.length})</span>
                        </h3>
                      </div>
                      <ChevronRight
                        size={16}
                        className={`text-cocoa transition-transform ${!collapsedSections.replied ? "rotate-90" : ""}`}
                      />
                    </button>
                    {!collapsedSections.replied && renderContactGrid(groupedContacts.replied)}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ============ COFFEE CHATS ============ */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <Coffee size={18} className="text-caramel" />
              <h2 className="text-lg font-semibold text-espresso">Coffee Chats</h2>
            </div>
            {/* <p className="text-sm text-cocoa mb-5">Networking conversations you're preparing for.</p> */}

            {!hasChats ? (
              <div className="text-center py-12 bg-cream rounded-2xl border-2 border-dashed border-sand">
                <Coffee size={40} className="mx-auto text-caramel mb-4" />
                <p className="text-cocoa mb-4">No coffee chats yet. Start networking!</p>
                <button
                  onClick={() => {
                    setEditingChat(null);
                    setShowAddChat(true);
                  }}
                  className="inline-flex items-center gap-2 text-caramel hover:underline"
                >
                  <SquarePlus size={16} />
                  Schedule your first coffee chat
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {groupedChats.pending.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
                      <h3 className="text-sm font-semibold text-espresso tracking-wide">
                        Pending <span className="text-cocoa font-normal">({groupedChats.pending.length})</span>
                      </h3>
                    </div>
                    {renderChatGrid(groupedChats.pending)}
                  </div>
                )}

                {groupedChats.sent.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500" />
                      <h3 className="text-sm font-semibold text-espresso tracking-wide">
                        Sent <span className="text-cocoa font-normal">({groupedChats.sent.length})</span>
                      </h3>
                    </div>
                    {renderChatGrid(groupedChats.sent)}
                  </div>
                )}

              </div>
            )}
          </section>

          {/* ============ ARCHIVE (shared) ============ */}
          {(archivedContacts.length > 0 || groupedChats.done.length > 0) && (
            <section>
              <button
                onClick={() => setShowArchive(!showArchive)}
                className="flex items-center gap-2 mb-4 text-cocoa hover:text-espresso transition"
              >
                <Archive size={18} />
                <h2 className="text-lg font-semibold">
                  Archive{" "}
                  <span className="font-normal text-cocoa">
                    ({archivedContacts.length + groupedChats.done.length})
                  </span>
                </h2>
                <ChevronRight size={18} className={`transition-transform ${showArchive ? "rotate-90" : ""}`} />
              </button>

              {showArchive && (
                <div className="space-y-8 opacity-70">
                  {archivedContacts.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Mail size={14} className="text-cocoa" />
                        <h3 className="text-[15px] font-semibold text-espresso">
                          Cold Emails <span className="text-cocoa font-normal">({archivedContacts.length})</span>
                        </h3>
                      </div>
                      {renderContactGrid(archivedContacts)}
                    </div>
                  )}

                  {groupedChats.done.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Coffee size={14} className="text-cocoa" />
                        <h3 className="text-[15px] font-semibold text-espresso">
                          Coffee Chats <span className="text-cocoa font-normal">({groupedChats.done.length})</span>
                        </h3>
                      </div>
                      {renderChatGrid(groupedChats.done)}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* ADD TYPE CHOOSER */}
      {showTypeChooser && (
        <AddTypeChooser
          onClose={() => setShowTypeChooser(false)}
          onPickColdEmail={() => {
            setShowTypeChooser(false);
            setEditingContact(null);
            setShowAddContact(true);
          }}
          onPickCoffeeChat={() => {
            setShowTypeChooser(false);
            setEditingChat(null);
            setShowAddChat(true);
          }}
        />
      )}

      {/* COLD EMAIL MODALS */}
      <AddContactModal
        isOpen={showAddContact}
        onClose={() => {
          setShowAddContact(false);
          setEditingContact(null);
        }}
        onAdd={handleAddContact}
        editContact={editingContact || undefined}
      />

      {showEmailEditor && selectedContact && (
        <EmailEditorModal
          contact={selectedContact}
          onClose={() => {
            setShowEmailEditor(false);
            setSelectedContact(null);
          }}
          onSave={(content, status) => handleSaveEmail(selectedContact.id, content, status)}
        />
      )}

      {/* COFFEE CHAT MODALS */}
      <AddCoffeeChat
        isOpen={showAddChat}
        onClose={() => {
          setShowAddChat(false);
          setEditingChat(null);
        }}
        editChat={editingChat || undefined}
      />

      {selectedChat && (
        <ChatDetailsModal
          chat={selectedChat}
          onClose={() => setSelectedChat(null)}
          onEdit={() => {
            setEditingChat(selectedChat);
            setShowAddChat(true);
          }}
        />
      )}
    </div>
  );
}
